import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { Component, EventEmitter, Input, Output, input, model } from '@angular/core';

import { ComoService } from '@core/services/como/como.service';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';
import { UserService } from '@core/services/user/user.service';
import { UserCredential } from 'firebase/auth';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { getAllCountries } from '@shared/components/input-country-phone/config/countries.config';

@Component({
  selector: 'app-phone-number-step',
  templateUrl: './phone-number-step.component.html',
})
export class PhoneNumberStepComponent extends DestroyerBase {
  readonly phone = model<string>('');
  readonly useLocalFormat = input<boolean>(false);
  readonly defaultRegionCode = input<string>('fr');

  @Output() onClose = new EventEmitter<Event>();
  @Output() goToNextStep = new EventEmitter<void>();
  @Output() goToAuthenticationStep = new EventEmitter<void>();
  @Output() goToAccountNotFoundStep = new EventEmitter<void>();
  @Output() goToKioskAccountNotFoundStep = new EventEmitter<void>();

  @Input() accentColor?: ContentBuilderFieldColors;

  isValidating = false;
  errorMessage?: string;

  kioskParameters = this.settingsSelector.kiosk;

  constructor(
    private readonly comoService: ComoService,
    private readonly authFirebaseService: AuthFirebaseService,
    private readonly userService: UserService,
    private readonly settingsSelector: SettingsStoreSelector,
  ) {
    super();
  }

  onRegister(): void {
    const phone = this.phone();
    if (!phone) {
      return;
    }
    this.isValidating = true;
    this.errorMessage = undefined;
    this.comoService
      .linkComoUser$({ phoneOrEmail: phone })
      .pipe(
        takeUntil(this._destroyerRef),
        tap(response => {
          if (response.otp_sent) {
            this.goToNextStep.next();
          } else {
            this.goToAuthenticationStep.next();
          }
        }),
      )
      .subscribe({
        error: error => this.handleErrorResponse(error.error, this.phone()),
        complete: () => (this.isValidating = false),
      });
  }

  anonymousLogin(): void {
    this.authFirebaseService
      .signInAnonymously()
      .pipe(
        switchMap((userCredential: UserCredential) =>
          this.authFirebaseService.saveFirebaseTokenInSessionStore$(userCredential),
        ),
        switchMap(() => this.userService.createAnonymousUser$()),
        switchMap(() => this.authFirebaseService.loadUser$()),
      )
      .subscribe();
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.phone();
    this.phone.set(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.phone();
    const defaultRegionCode = this.defaultRegionCode() || 'fr';

    // Get the default dial code based on the region
    const defaultDialCode = this.getDefaultDialCode(defaultRegionCode);
    // Don't allow deletion if it would remove part of the default dial code
    // or if the current value is already just the dial code
    if (currentValue.length <= defaultDialCode.length || currentValue === defaultDialCode) {
      return;
    }

    this.phone.set(currentValue.slice(0, -1));
  }

  private getDefaultDialCode(regionCode: string): string {
    // Get all countries and create a map from iso2 to dial code
    const countries = getAllCountries();
    const country = countries.find(c => c.iso2.toLowerCase() === regionCode.toLowerCase());

    if (country) {
      return `+${country.dialCode}`;
    }

    // Default to France if region code not found
    return '+33';
  }

  private handleErrorResponse(error: any, _phone: string): void {
    if (error.code === 202) {
      if (!this.kioskParameters.isKioskLayout) {
        this.goToAccountNotFoundStep.next();
      } else {
        this.goToKioskAccountNotFoundStep.next();
      }
    }
    this.errorMessage = this.comoService.getErrorMessageFromCode(error.code);
  }

  onUpdatePhone(phone: string) {
    this.phone.set(phone);
  }

  public handleKeyDown(event: KeyboardEvent): void {
    // Prevent default action for Enter key to avoid triggering the first button
    if (event.key === 'Enter') {
      event.preventDefault();
      // Optionally trigger form submission if the form is valid
      if (this.phone()) {
        this.onRegister();
      }
    }
  }
}
